/**
 * productSaleState — v149.69.
 *
 * What a storefront card needs to know about one product before it draws
 * a price or a button: can it be bought here, is it a service or a
 * ticket, is it inside its availability window, are tickets left.
 *
 * Moved out of `StorefrontCard` unchanged so the classic card and the
 * compact tile (`CompactStorefront`) answer from ONE copy of the rules.
 * Change a rule here, not in a card.
 */
export const CURRENCY_SYMBOLS = { INR: "₹", USD: "$", EUR: "€", GBP: "£" };

const localISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

/** "2026-10-02" → "Oct 2" in the visitor's locale. */
export const formatShortDate = (iso) => {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return iso; }
};

export function getProductSaleState(product, now = new Date()) {
  const currency = String(product.currency || "INR").toUpperCase();
  const isINR = currency === "INR";
  const sym = CURRENCY_SYMBOLS[currency] || "₹";
  const hasPrice = Number(product.price) > 0;
  // Linkola checkout: an INR price and no external buy link.
  const canBuyNow = isINR && hasPrice && !product.buy_url;
  const kind = String(product.kind || "product").toLowerCase();
  // v111 — Services book a slot; v147.58 — tickets are events with capacity.
  const isService = kind === "service";
  const isTicket = kind === "ticket";
  const totalTickets = Number(product.total_tickets || 0);
  const ticketsSold = Number(product.tickets_sold || 0);
  const ticketsLeft = isTicket && totalTickets > 0
    ? Math.max(0, totalTickets - ticketsSold)
    : null;
  const isTicketSoldOut = isTicket && totalTickets > 0 && ticketsLeft === 0;
  // v147.53 — Availability window pinned by the creator (single day or a
  // date range). Outside it the Buy / Book button is blocked.
  const winFrom = (product.available_from || "").slice(0, 10) || null;
  const winTo = (product.available_to || "").slice(0, 10) || null;
  const today = localISO(now);
  let availStatus = "unlimited";
  if (winFrom || winTo) {
    if (winFrom && today < winFrom) availStatus = "not_started";
    else if (winTo && today > winTo) availStatus = "ended";
    else availStatus = "active";
  }
  const availBlocked = availStatus === "not_started" || availStatus === "ended";
  const availLabel =
    availStatus === "unlimited" ? ""
    : availStatus === "ended" ? `${isService ? "Bookings" : "Sale"} ended ${formatShortDate(winTo)}`
    : availStatus === "not_started"
      ? (winTo && winTo !== winFrom ? `Opens ${formatShortDate(winFrom)}` : `Available ${formatShortDate(winFrom)}`)
    : (winFrom && winTo && winFrom !== winTo)
      ? `Available ${formatShortDate(winFrom)} – ${formatShortDate(winTo)}`
    : (winFrom && winTo && winFrom === winTo)
      ? `Available today only`
    : winFrom ? `From ${formatShortDate(winFrom)}` : `Until ${formatShortDate(winTo)}`;
  return {
    isINR, sym, hasPrice, canBuyNow, isService, isTicket,
    totalTickets, ticketsSold, ticketsLeft, isTicketSoldOut,
    winFrom, winTo, availStatus, availBlocked, availLabel,
  };
}
